import consumer from '../../channels/consumer';
import SpeakApiClient from './speak_api_client';
import AudioPlayerService from './audio_player_service';
import { shouldShowPhraseIdeas, promptItemsFor, flashcardPayload } from './phrase_ideas';
import PhraseIdeasRequest from './phrase_ideas_request';
import { element, icon, button, renderPhraseRows, PhraseAudio } from '../../components/shared/phrase_rows';

const dialogs = new WeakMap();
const t = key => window.I18n.t(`javascript.pre_chat_practice.${key}`);
const shared = key => window.I18n.t(key);
// One phrase is one saved card: the same text and translation are never saved twice.
function phraseKey({ phrase, translation }) {
  return JSON.stringify([phrase, translation || '']);
}

// Shared by Speak starts and My Path; explicit previews bypass only the gate.
export function startWithPhraseIdeas(context, continueStart, { force = false } = {}) {
  const container = document.querySelector('[data-speak-page="true"]');
  const configEl = container?.querySelector('.js-speak-page-config');
  const config = configEl ? JSON.parse(configEl.textContent) : {};
  const practice = config.pre_chat_practice || {};
  const promptItems = promptItemsFor(context);
  // The Speak settings form's hidden field (one per page) holds an unsaved switch change.
  const settingInput = document.querySelector('.js-phrase-setting-value');
  const setting = settingInput && !settingInput.disabled ? settingInput.value : practice.setting;
  if (!force && !shouldShowPhraseIdeas({
    ...practice, setting, category: promptItems[0], topicKey: promptItems[promptItems.length - 1],
    // A two-part path (category, topic) has no subtopic; only a three-part path does.
    subtopic: promptItems.length > 2 ? promptItems[1] : undefined,
    manualVocab: context.manualVocab, customConversation: context.customConversation
  })) return continueStart();
  // Without the dialog markup (a caller outside the Speak page) start as before.
  if (!document.getElementById('phraseIdeasModal')) return continueStart();

  const dialog = dialogFor(container, config);
  // A second click while the dialog is up must not reset it under the first.
  if (dialog.showing) return;
  dialog.open(context.title, promptItems, continueStart);
}

// The same dialog for phrases the caller already has (cards in the learner's
// vocabulary): no phrase request and no Save buttons; the rows, translations,
// audio, focus handling and the Start button are the same. Without the dialog
// markup (a page other than Speak) it continues at once.
// phrases: [{ front, back }]
export function startWithGivenPhrases({ title, phrases, onContinue }) {
  const container = document.querySelector('[data-speak-page="true"]');
  if (!container || !document.getElementById('phraseIdeasModal')) return onContinue();
  const configEl = container.querySelector('.js-speak-page-config');
  const dialog = dialogFor(container, configEl ? JSON.parse(configEl.textContent) : {});
  if (dialog.showing) return;
  dialog.open(title, null, onContinue, phrases.map(({ front, back }) => ({ phrase: front, translation: back })));
}

function dialogFor(container, config) {
  let dialog = dialogs.get(container);
  if (!dialog) { dialog = new PhraseIdeasDialog(container, config); dialogs.set(container, dialog); }
  return dialog;
}

class PhraseIdeasDialog {
  constructor(container, config) {
    this.config = config;
    this.api = new SpeakApiClient(container.dataset.authToken);
    this.modal = document.getElementById('phraseIdeasModal');
    this.$modal = $(this.modal);
    this.status = this.modal.querySelector('.js-phrase-status');
    this.list = this.modal.querySelector('.js-phrase-list');
    this.save = this.modal.querySelector('.js-phrase-save');
    this.start = this.modal.querySelector('.js-phrase-start');
    this.recentToggle = this.modal.querySelector('.js-phrase-recent-toggle');
    this.recent = this.modal.querySelector('#phraseIdeasRecent');
    this.recentList = this.modal.querySelector('.js-phrase-recent-list');
    this.recentStatus = this.modal.querySelector('.js-phrase-recent-status');
    this.actionError = this.modal.querySelector('.js-phrase-action-error');
    const audioPlayer = new AudioPlayerService(document.querySelector('meta[name="csrf-token"]')?.content);
    this.phraseAudio = new PhraseAudio({
      // ElevenLabs on purpose, whatever the learner's chat voice: mobile's phrase screen
      // uses ElevenLabs TTS too, so phrase audio sounds the same on both.
      load: async phrase => {
        const { audioUrl, playbackRate } = await audioPlayer.fetchAudioData(phrase, this.config.current_language, { voice_provider: 'elevenlabs' });
        return { audio: new Audio(audioUrl), release: () => URL.revokeObjectURL(audioUrl), playbackRate };
      },
      onStart: () => { this.actionError.textContent = ''; },
      onError: () => { this.actionError.textContent = shared('javascript.errors.audio_unavailable'); }
    });
    this.request = new PhraseIdeasRequest({
      post: promptItems => this.api.request('/api/v1/pre_chat_practice', { method: 'POST', body: { promptItems } }),
      get: id => this.api.request(`/api/v1/pre_chat_practice/${encodeURIComponent(id)}`),
      // The channel resolves the session user; no user_id parameter is needed.
      subscribe: received => consumer.subscriptions.create('PreChatPracticeChannel', { received }),
      onState: state => this.renderState(state)
    });
    this.save.addEventListener('click', () => this.saveAll());
    this.start.addEventListener('click', () => {
      this.pendingStart = this.continueStart;
      this.start.disabled = true;
      this.$modal.modal('hide');
    });
    this.recentToggle.addEventListener('click', () => this.toggleRecent());
    // A Start click during Bootstrap's open transition is dropped by modal('hide');
    // finish the hide once the dialog is shown so the chat still starts.
    this.$modal.on('shown.bs.modal', () => {
      if (this.pendingStart) this.$modal.modal('hide');
    });
    this.$modal.on('hide.bs.modal', () => this.cancel());
    this.$modal.on('hidden.bs.modal', () => {
      this.showing = false;
      this.returnFocus?.focus();
      const start = this.pendingStart;
      this.pendingStart = null;
      start?.();
    });
    this.modal.addEventListener('keydown', event => this.trapFocus(event));
    this.beforeCache = () => { this.pendingStart = null; this.$modal.modal('hide'); this.cancel(); };
    document.addEventListener('turbolinks:before-cache', this.beforeCache, { once: true });
  }

  // given: phrases to show instead of asking for ideas (startWithGivenPhrases).
  open(title, promptItems, continueStart, given = null) {
    this.cancel();
    this.given = !!given;
    this.opened = true;
    this.showing = true;
    this.returnFocus = document.activeElement;
    this.continueStart = continueStart;
    this.pendingStart = null;
    this.promptItems = promptItems;
    this.phrases = [];
    this.saved = new Set();
    this.saving = false;
    this.recentLoaded = false;
    this.recentLoading = false;
    this.recent.hidden = true;
    this.recentList.replaceChildren();
    this.recentStatus.replaceChildren();
    this.recentToggle.setAttribute('aria-expanded', 'false');
    this.recentToggle.textContent = t('show_recent_vocabulary');
    this.actionError.textContent = '';
    this.modal.querySelector('#phraseIdeasTitle').textContent = title || t('phrase_ideas');
    this.start.disabled = false;
    this.isCall = document.getElementById('chat_form_mode')?.value === 'call_mode';
    this.modal.querySelector('.chat-speak-phrase-heading').hidden = this.given;
    this.$modal.modal({ backdrop: true, keyboard: true, focus: true, show: true });
    if (given) this.renderState({ phrases: given });
    else this.request.start(promptItems);
  }

  cancel() {
    this.opened = false;
    this.version = (this.version || 0) + 1;
    this.request.cancel();
    this.phraseAudio.stop();
  }

  renderState({ loading = false, phrases = [], error }) {
    this.loading = loading;
    this.phrases = phrases;
    this.status.replaceChildren();
    this.list.replaceChildren();
    this.save.hidden = !!error;
    this.save.disabled = !phrases.length;
    this.save.classList.remove('is-saved');
    this.save.replaceChildren(icon('plus'), document.createTextNode(t('save_all')));
    this.start.textContent = t(`${phrases.length ? '' : 'skip_'}start_${this.isCall ? 'call' : 'chat'}`);
    // Given phrases are already in the learner's vocabulary.
    this.recentToggle.hidden = !phrases.length || this.given;
    if (loading) {
      this.status.className = 'js-phrase-status chat-speak-phrase-status';
      this.status.append(icon('spinner fa-spin'), element('span', '', t('generating_phrases')));
    } else if (error) {
      this.status.className = 'js-phrase-status chat-speak-phrase-status';
      this.status.append(icon('exclamation-triangle'), element('span', '', t(error)),
        button(t('try_again'), 'chat-speak-phrase-link', () => this.request.start(this.promptItems)));
    } else {
      this.status.className = 'js-phrase-status';
      this.renderRows(this.list, phrases, { savable: !this.given });
    }
  }

  renderRows(list, phrases, { savable = false } = {}) {
    if (savable) this.saveButtons = new Map();
    renderPhraseRows(list, phrases, {
      labels: {
        show: shared('javascript.call_mode_bar.show_translation'),
        hide: shared('javascript.call_mode_bar.hide_translation'),
        play: shared('javascript.call_mode_bar.play_audio')
      },
      onPlay: (item, audio) => this.phraseAudio.toggle(item.phrase, audio),
      // Web has room for a per-phrase save next to translate and audio (Don, 16 Oct).
      extra: savable && (item => {
        const key = phraseKey(item);
        const save = button('', 'chat-speak-phrase-icon chat-speak-phrase-add', () => this.savePhrase(item));
        this.saveButtons.set(key, save);
        this.paintSaveButton(key);
        return save;
      })
    });
  }

  paintSaveButton(key, busy = false) {
    const save = this.saveButtons?.get(key);
    if (!save) return;
    const saved = this.saved.has(key);
    save.classList.toggle('is-saved', saved);
    save.disabled = saved || busy || this.saving;
    save.replaceChildren(icon(busy ? 'spinner fa-spin' : saved ? 'check' : 'plus'));
    save.setAttribute('aria-label', saved ? t('saved') : t('save_phrase'));
  }

  paintSaveAll() {
    const allSaved = this.phrases.length > 0 && this.phrases.every(item => this.saved.has(phraseKey(item)));
    this.save.classList.toggle('is-saved', allSaved);
    this.save.disabled = allSaved || this.saving || !this.phrases.length;
    this.save.replaceChildren(icon(allSaved ? 'check' : 'plus'), document.createTextNode(t(allSaved ? 'saved' : 'save_all')));
  }

  async savePhrase(item) {
    const key = phraseKey(item);
    if (this.saving || this.saved.has(key)) return;
    const version = this.version;
    this.saving = true;
    this.saveButtons.forEach((_, other) => this.paintSaveButton(other, other === key));
    this.save.disabled = true;
    this.actionError.textContent = '';
    try {
      await this.api.request('/api/v1/flashcards', { method: 'POST', body: flashcardPayload(item, this.config.current_language) });
      if (version !== this.version) return;
      this.saved.add(key);
      this.recentLoaded = false;
      if (!this.recent.hidden) this.loadRecent();
    } catch (error) {
      console.error(error);
      if (version !== this.version) return;
      this.actionError.textContent = shared('javascript.errors.failed_add_vocabulary');
    } finally {
      if (version === this.version) {
        this.saving = false;
        this.saveButtons.forEach((_, other) => this.paintSaveButton(other));
        this.paintSaveAll();
      }
    }
  }

  async saveAll() {
    if (this.saving || !this.phrases.length) return;
    const version = this.version;
    this.saving = true;
    this.save.disabled = true;
    this.save.replaceChildren(icon('spinner fa-spin'), document.createTextNode(t('saving')));
    this.actionError.textContent = '';
    try {
      // Sequential, as mobile. Keep successful rows on a partial failure so a retry
      // only sends the remaining cards, and never claim Saved on an HTTP failure.
      for (const phrase of this.phrases) {
        if (version !== this.version) return;
        const key = phraseKey(phrase);
        if (this.saved.has(key)) continue;
        await this.api.request('/api/v1/flashcards', { method: 'POST', body: flashcardPayload(phrase, this.config.current_language) });
        if (version !== this.version) return;
        this.saved.add(key);
        this.paintSaveButton(key);
      }
      this.save.classList.add('is-saved');
      this.save.replaceChildren(icon('check'), document.createTextNode(t('saved')));
      this.recentLoaded = false;
      if (!this.recent.hidden) this.loadRecent();
    } catch (error) {
      console.error(error);
      if (version !== this.version) return;
      this.actionError.textContent = shared('javascript.errors.failed_add_vocabulary');
      this.save.replaceChildren(icon('plus'), document.createTextNode(t('save_all')));
      this.save.disabled = false;
    } finally {
      if (version === this.version) {
        this.saving = false;
        this.saveButtons?.forEach((_, key) => this.paintSaveButton(key));
      }
    }
  }

  toggleRecent() {
    this.recent.hidden = !this.recent.hidden;
    this.recentToggle.setAttribute('aria-expanded', String(!this.recent.hidden));
    this.recentToggle.textContent = t(`${this.recent.hidden ? 'show' : 'hide'}_recent_vocabulary`);
    if (!this.recent.hidden) this.loadRecent();
  }

  async loadRecent() {
    if (this.recentLoaded || this.recentLoading) return;
    const version = this.version;
    this.recentLoading = true;
    this.recentStatus.textContent = t('loading_vocabulary');
    try {
      const query = new URLSearchParams({ language: this.config.current_language, limit: '20' });
      const data = await this.api.request(`/api/v1/flashcards?${query}`);
      if (version !== this.version) return;
      this.recentLoaded = true;
      this.renderRows(this.recentList, data.flashcards.map(card => ({ phrase: card.front, translation: card.back })));
      this.recentStatus.textContent = data.flashcards.length ? '' : t('no_vocabulary_found');
    } catch (error) {
      console.error(error);
      if (version !== this.version) return;
      this.recentStatus.replaceChildren(element('span', '', shared('javascript.chat.error_loading_vocabulary')),
        button(t('try_again'), 'chat-speak-phrase-link', () => this.loadRecent()));
    } finally {
      if (version === this.version) this.recentLoading = false;
    }
  }

  trapFocus(event) {
    if (event.key !== 'Tab') return;
    const buttons = [...this.modal.querySelectorAll('button:not([disabled])')].filter(el => el.offsetParent !== null);
    const first = buttons[0], last = buttons[buttons.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === this.modal)) {
      event.preventDefault(); last?.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault(); first?.focus();
    }
  }
}

// Keep an untouched nil preference nil: its smart default depends on the chosen
// topic. A deliberate switch change is submitted just like the other settings.
document.addEventListener('turbolinks:load', () => {
  const checkbox = document.getElementById('pre-chat-practice-setting');
  const value = document.querySelector('.js-phrase-setting-value');
  if (!checkbox || !value) return;
  checkbox.addEventListener('change', () => {
    value.disabled = false;
    value.value = checkbox.checked ? 'true' : 'false';
  });
  // Rendered disabled; usable only once the change is wired to the form field.
  checkbox.disabled = false;
});
